import { effectiveContent, isContentPreview } from './file-record';
import type { WorkspaceFile } from './workspace-files';

// Resolving a `vfsPath` attachment — a file the AGENT authored — to the same workspace file the gallery
// shows. The harness attaches such files BY PATH with an empty `url` (see `attach-file-tool.ts`
// `toAttachment`), because the bytes already live in our own file system and there is nothing to upload.
// The client is expected to resolve the path against the workspace files it already loads and open that
// file's content — the "content that is a bare URL" the doc viewer / `artifact-file.ts` already handle.
//
// All three surfaces resolve `vfsPath` now — iOS `SymphonyData.artifactContent` and Android
// `SymphonyApi.resolveVfsArtifactContent` do the same slug→id→file walk. Keep the matching rule below
// identical to theirs; a change here is a change on both phones.
//
// The mapping (both sides two views of ONE stored file — see `vfs-tools.ts` / `assistant-materialize.ts`
// on the harness side and `pages/api/workspace-files.ts` on this side):
//   harness vfsPath      = workspaces/<slug>/artifacts/<rest>   (the agent only ever knows slugs)
//   storage key          = <workspaceId>/artifacts/<rest>       (the stable id)
//   web WorkspaceFile.name = <workspaceId>/… minus the id prefix = artifacts/<rest>
// So a vfsPath resolves by: take its slug (2nd segment) → the workspace id it maps to (`useWorkspaces`) →
// that workspace's files → the one whose `name` is the vfsPath with its `workspaces/<slug>/` prefix removed.

/** The `{ slug, name }` a `workspaces/<slug>/<name…>` vfsPath addresses — `slug` to resolve to a workspace
 *  id, `name` to match a `WorkspaceFile.name` (which is the stored `<workspaceId>/<path>` key minus its id
 *  prefix, i.e. this same `name`). Null when `vfsPath` isn't a `workspaces/<slug>/<rest>` path. */
/** The storage key of a meeting file attached as `transcripts/<key>`, or null for any other path. The
 *  TRANSCRIPTS directory is the account's own rather than a Space's, so it has no slug to resolve. */
export function transcriptsKeyOf(vfsPath: string): string | null {
  const path = vfsPath.trim();
  if (!path.startsWith('transcripts/')) return null;
  const key = path.slice('transcripts/'.length);
  return key && !key.split('/').some((segment) => segment === '' || segment === '..' || segment === '.') ? key : null;
}

export function workspaceFileRefOf(vfsPath: string): { slug: string; name: string } | null {
  const parts = vfsPath.trim().split('/');
  if (parts.length < 3 || parts[0] !== 'workspaces') return null;
  const slug = parts[1];
  const name = parts.slice(2).join('/');
  return slug && name ? { slug, name } : null;
}

/** The content of the workspace file a `vfsPath` points at, or null when it can't be resolved from `files`
 *  (not a workspace path, or no file with that exact name / no usable content). Exact-name match: the
 *  vfsPath→name transform is a contract, and a looser basename match could collide across folders.
 *
 *  Read through `effectiveContent`, never `file.content`: a file whose bytes live elsewhere keeps
 *  `content` EMPTY and addresses them through `reference.url`, which is every binary an agent attaches —
 *  a rendered PNG, an exported PDF — plus every upload. Reading `content` raw resolved those to nothing
 *  and the viewer said the file "isn't available to view here" next to a Download that worked fine. */
export function resolveVfsContent(vfsPath: string, files: WorkspaceFile[]): string | null {
  const ref = workspaceFileRefOf(vfsPath);
  if (!ref) return null;
  const match = files.find((file) => !file.isApp && file.name === ref.name);
  // A listed row whose body was cut to its head is not this file's content — answering with it would open,
  // and let the editor save, a preview. Unresolved here sends every caller on to the scoped
  // `/api/workspace-file` read, which returns the whole body.
  if (match && isContentPreview(match)) return null;
  const body = match ? effectiveContent(match) : undefined;
  return body?.trim() ? body : null;
}
